/** System status → Storage: objects per tier, each location's contents and disk, and recent growth. */
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { bytes } from '../../../lib/format';
import { TIER_LABEL } from '../../../lib/tiers';
import { diskBelowQuotaNote, quotaPercent, quotaUsedLabel } from '../../../lib/quota';

interface StorageDTO {
  tiers: Array<{ tier: string; location: string | null; files: number; bytes: number }>;
  locations: Array<{ id: string; name: string; kind: string; site: string; availability: string; mode: string | null; capacity: { freeBytes: number; totalBytes: number } | null; quota: { limitBytes: number; usedBytes: number; state: 'ok' | 'warning' | 'full' } | null; files: number; bytes: number; byKind: Record<string, { files: number; bytes: number }>; tiers: string[] }>;
  growth: Array<{ day: string; files: number; bytes: number }>;
  totals: { managedFiles: number; managedBytes: number; inPlaceFiles: number; inPlaceBytes: number; missingFiles: number };
}

export function StorageView() {
  const q = useQuery({ queryKey: ['system-storage'], queryFn: () => api<StorageDTO>('/api/v1/system/storage'), refetchInterval: 30_000 });
  const d = q.data;
  if (!d) return <div className="skeleton" style={{ height: 160 }} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="storage-view">
      <section className="panel-card">
        <p className="section-label">By tier</p>
        <table className="table compact">
          <thead><tr><th>Tier</th><th>Placed in</th><th>Objects</th><th>Size</th></tr></thead>
          <tbody>{d.tiers.map((t) => (
            <tr key={t.tier}><td style={{ fontWeight: 550 }}>{TIER_LABEL[t.tier] ?? t.tier}</td><td className="muted">{t.location ?? '—'}</td><td className="mono">{t.files.toLocaleString()}</td><td className="mono">{bytes(t.bytes)}</td></tr>
          ))}</tbody>
        </table>
        <span className="help">Objects of each kind wherever they live now (files stay where they were stored when a tier changes). PRISM manages {d.totals.managedFiles.toLocaleString()} files ({bytes(d.totals.managedBytes)}); {d.totals.inPlaceFiles.toLocaleString()} more ({bytes(d.totals.inPlaceBytes)}) are catalogued in place.{d.totals.missingFiles ? ` ${d.totals.missingFiles} files are missing.` : ''}</span>
      </section>
      <div className="cards">
        {d.locations.map((l) => {
          const used = l.capacity ? l.capacity.totalBytes - l.capacity.freeBytes : null;
          return (
            <section className="panel-card" key={l.id}>
              <h3>{l.name} <span className="faint" style={{ fontWeight: 400, fontSize: 12 }}>{l.kind}{l.mode ? ` · ${l.mode.replace('_', ' ')}` : ''} · {l.site}</span></h3>
              {l.tiers.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{l.tiers.map((t) => <span className="pill accent" key={t}>{TIER_LABEL[t]}</span>)}</div>}
              <dl className="kv">
                {Object.entries(l.byKind).sort((a, b) => b[1].bytes - a[1].bytes).map(([k, v]) => <Fragment key={k}><dt>{k}</dt><dd>{v.files.toLocaleString()} · {bytes(v.bytes)}</dd></Fragment>)}
                <dt>Total</dt><dd style={{ fontWeight: 600 }}>{l.files.toLocaleString()} · {bytes(l.bytes)}</dd>
                {l.capacity && <><dt>Disk</dt><dd>{bytes(l.capacity.freeBytes)} free of {bytes(l.capacity.totalBytes)}
                  <div className="progress" style={{ marginTop: 4 }}><span style={{ width: `${Math.min(100, ((used ?? 0) / Math.max(1, l.capacity.totalBytes)) * 100)}%` }} /></div></dd></>}
                {l.quota && <><dt>Quota</dt><dd data-testid="status-quota">{quotaUsedLabel(l.quota)}
                  {l.quota.state !== 'ok' && <span className={`pill ${l.quota.state === 'full' ? 'danger' : 'warning'}`} style={{ marginLeft: 6 }}>{l.quota.state === 'full' ? 'Full' : 'Over 90%'}</span>}
                  <div className={`progress ${l.quota.state === 'ok' ? '' : l.quota.state}`} style={{ marginTop: 4 }}><span style={{ width: `${quotaPercent(l.quota)}%` }} /></div>
                  {diskBelowQuotaNote(l.quota, l.capacity?.freeBytes) && <span className="help" style={{ color: 'var(--warning)', display: 'block', marginTop: 4 }}>{diskBelowQuotaNote(l.quota, l.capacity?.freeBytes)}</span>}</dd></>}
              </dl>
            </section>
          );
        })}
      </div>
      <section className="panel-card">
        <p className="section-label">Stored in the last 14 days</p>
        <table className="table compact">
          <thead><tr><th>Day</th><th>Files</th><th>Size</th></tr></thead>
          <tbody>{[...d.growth].reverse().map((g) => <tr key={g.day}><td className="mono">{g.day}</td><td className="mono">{g.files}</td><td className="mono">{bytes(g.bytes)}</td></tr>)}
            {d.growth.length === 0 && <tr><td colSpan={3} className="muted">Nothing stored recently.</td></tr>}</tbody>
        </table>
      </section>
    </div>
  );
}
